import React from 'react'
import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'

const RequirementsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 5px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin: 8px 0;
  background-color: #f9f9f9;
`

const RequirementRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`

const StyledInfoIcon = styled(InfoIcon)`
  color: #888;
  font-size: 16px;
`

type PasswordRequirementsProps = {
  password: string
  newPassword: string
  touched: boolean
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  newPassword,
  touched,
}) => {
  const requirements = [
    {
      id: 'length',
      text: 'At least 8 characters',
      isValid: (pwd: string) => pwd.length >= 8,
    },
    {
      id: 'lowercase',
      text: 'At least one lowercase letter',
      isValid: (pwd: string) => /[a-z]/.test(pwd),
    },
    {
      id: 'uppercase',
      text: 'At least one uppercase letter',
      isValid: (pwd: string) => /[A-Z]/.test(pwd),
    },
    {
      id: 'number',
      text: 'At least one number',
      isValid: (pwd: string) => /\d/.test(pwd),
    },
    {
      id: 'special',
      text: 'At least one special character',
      isValid: (pwd: string) => /[^a-zA-Z\d]/.test(pwd),
    },
    {
      id: 'match',
      text: 'Passwords must match',
      // Only validate match if both passwords have values
      isValid: (pwd: string, confirmPwd?: string) =>
        !confirmPwd ? true : pwd === confirmPwd,
    },
  ]

  return (
    <RequirementsContainer>
      <Box display="flex" alignItems="center" mb={1}>
        <StyledInfoIcon />
        <Typography ml={1} fontWeight="medium" fontSize="13px">
          Password Requirements
        </Typography>
      </Box>

      {requirements.map((req) => {
        const isValid =
          req.id === 'match'
            ? req.isValid(password, newPassword)
            : req.isValid(password)

        const isInvalid =
          touched &&
          !isValid &&
          (req.id !== 'match' || (req.id === 'match' && newPassword))

        let IconComponent = null
        let textColor = 'text.primary'

        if (
          password &&
          isValid &&
          (req.id !== 'match' || (req.id === 'match' && newPassword))
        ) {
          IconComponent = CheckCircleIcon
          textColor = 'success.main'
        } else if (
          (password || (req.id === 'match' && newPassword)) &&
          isInvalid
        ) {
          IconComponent = CancelIcon
          textColor = 'error.main'
        }

        return (
          <RequirementRow key={req.id}>
            <Box width="18px" height="18px">
              {IconComponent && (
                <IconComponent
                  fontSize="small"
                  color={isValid ? 'success' : 'error'}
                />
              )}
            </Box>
            <Typography color={textColor} fontSize="12px">
              {req.text}
            </Typography>
          </RequirementRow>
        )
      })}
    </RequirementsContainer>
  )
}

export default PasswordRequirements
